/**
 * Attribute authority — wire shapes and UI state.
 *
 * The HTTP contract is api/openapi/admin.yaml; these schemas validate what
 * comes back rather than redefining it, which is why every field here matches
 * a documented property name exactly.
 */

import { z } from "zod";

export const attributeClaimSchema = z.object({
  object_kind: z.string(),
  attribute: z.string(),
  source_id: z.string(),
  source_name: z.string(),
  source_kind: z.string(),
  /** True when PureID itself owns the attribute — the system-of-record case. */
  is_builtin: z.boolean(),
  /**
   * False while the owning source is disabled. The claim is retained and the
   * attribute is open until the source is active again, rather than writable by
   * nobody — so the surface has to distinguish "owned" from "owned and live".
   */
  binding: z.boolean(),
  owner_inactive_since: z.string().optional(),
  claimed_at: z.string(),
});

export const listAttributeClaimsResponseSchema = z.object({
  data: z.array(attributeClaimSchema),
});

/** Who an attribute may be given to; PureID itself comes first. */
export const attributeOwnerSchema = z.object({
  source_id: z.string(),
  name: z.string(),
  kind: z.string(),
  is_builtin: z.boolean(),
});
export type AttributeOwner = z.infer<typeof attributeOwnerSchema>;

export const attributeOwnersResponseSchema = z.object({
  data: z.array(attributeOwnerSchema),
});

export const coverageEntrySchema = z.object({
  object_kind: z.string(),
  registered: z.number(),
  claimed: z.number(),
  unclaimed: z.array(z.string()),
});

export const coverageResponseSchema = z.object({
  data: z.array(coverageEntrySchema),
});

export const releaseAllResponseSchema = z.object({ released: z.number() });

export type AttributeClaim = z.infer<typeof attributeClaimSchema>;
export type CoverageEntry = z.infer<typeof coverageEntrySchema>;

/** The object kinds that carry claims, matching internal/directory/attribute. */
export const OBJECT_KINDS = ["user", "group"] as const;
export type ObjectKind = (typeof OBJECT_KINDS)[number];

export const objectKindLabel: Record<ObjectKind, string> = {
  user: "People",
  group: "Groups",
};

/** URL state: which object kind's authority is on screen. */
export const authoritySearchSchema = z.object({
  kind: z.enum(OBJECT_KINDS).catch("user"),
});

export type AuthoritySearch = z.infer<typeof authoritySearchSchema>;

// ── Rollout: the enforcement mode and whether moving to enforce is safe ──

export const AUTHORITY_MODES = ["off", "observe", "enforce"] as const;
export type AuthorityMode = (typeof AUTHORITY_MODES)[number];

export const READINESS_STATUSES = [
  "ready",
  "violations_observed",
  "insufficient_evidence",
  "feeds_unbound",
  "not_observing",
] as const;
export type ReadinessStatus = (typeof READINESS_STATUSES)[number];

const transitionWindowSchema = z.object({
  ends_at: z.string().nullable(),
  open: z.boolean(),
});

export const authorityPolicySchema = z.object({
  mode: z.enum(AUTHORITY_MODES),
  mode_since: z.string(),
  enforce_since: z.string().nullable(),
  transition_window: transitionWindowSchema,
});

const violationSchema = z.object({
  object_kind: z.string(),
  attribute: z.string(),
  /** The attribute in words; empty when nothing could name it, so fall back to the key. */
  display_name: z.string(),
  source_id: z.string(),
  source_name: z.string(),
  /** Ten-minute observation windows, not writes. */
  windows: z.number(),
  first_observed_at: z.string(),
  last_observed_at: z.string(),
});

/**
 * One inbound connection and whether it names an authority. An unbound one
 * writes without reaching the gate, so nothing observes or counts it, and the
 * observed total beside it is short by however much it writes.
 */
const feedSchema = z.object({
  kind: z.enum(["scim_token", "ad_connector", "pull_connector"]),
  id: z.string(),
  name: z.string(),
  bound: z.boolean(),
  last_write_at: z.string().nullable(),
});

const unusableWriteSchema = z.object({
  source_id: z.string(),
  source_name: z.string(),
  windows: z.number(),
  last_at: z.string(),
});

export const rolloutResponseSchema = authorityPolicySchema.extend({
  readiness: z.object({
    status: z.enum(READINESS_STATUSES),
    lookback_days: z.number(),
    evidence_from: z.string(),
    checked_at: z.string(),
    observed_count: z.number(),
    unbound_feeds: z.number(),
  }),
  observed: z.array(violationSchema),
  refused: z.array(violationSchema),
  unusable_writes: z.array(unusableWriteSchema),
  feeds: z.array(feedSchema),
});

export type AuthorityPolicy = z.infer<typeof authorityPolicySchema>;
export type AuthorityRollout = z.infer<typeof rolloutResponseSchema>;
export type AuthorityViolation = z.infer<typeof violationSchema>;
export type AuthorityFeed = z.infer<typeof feedSchema>;

// ── Feed lag: how far downstream delivery is behind this tenant ──

export const feedLagSchema = z.object({
  consumer: z.string(),
  handled_through: z.number(),
  head: z.number(),
  behind: z.number(),
  oldest_unhandled_at: z.string().nullable(),
});

export const feedLagResponseSchema = z.object({ data: z.array(feedLagSchema) });

export type FeedLag = z.infer<typeof feedLagSchema>;
